import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Upload, ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
    { key: "name", label: "Name", required: true },
    { key: "roll_number", label: "Roll Number", required: true },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "payment_status", label: "Fee Status (e.g. Paid/Due — defaults to Due if blank)" },
    { key: "qr_code", label: "QR Code (optional, must already exist in the pool)" },
];

export default function StudentImportPage() {
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [buses, setBuses] = useState([]);
    const [file, setFile] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [preview, setPreview] = useState(null); // { sheets, common_headers, all_headers, suggested_map }
    const [columnMap, setColumnMap] = useState({});
    const [sheetSettings, setSheetSettings] = useState({}); // { [sheetName]: { department_override, bus_id } }
    const [importing, setImporting] = useState(false);
    const [results, setResults] = useState(null);

    useEffect(() => {
        api.get("/buses").then((r) => setBuses(r.data));
    }, []);

    const analyze = async () => {
        const f = fileRef.current?.files?.[0];
        if (!f) {
            toast.error("Choose an Excel file first");
            return;
        }
        setFile(f);
        setAnalyzing(true);
        setResults(null);
        try {
            const fd = new FormData();
            fd.append("file", f);
            // Do NOT set Content-Type manually — axios needs to generate its own
            // multipart boundary, which a hardcoded header value strips out.
            const { data } = await api.post("/students/import/preview", fd);
            setPreview(data);
            setColumnMap({ ...data.suggested_map });
            const settings = {};
            data.sheets.forEach((s) => {
                settings[s.name] = { department_override: s.name, bus_id: "" };
            });
            setSheetSettings(settings);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not read file");
        } finally {
            setAnalyzing(false);
        }
    };

    const updateSheetSetting = (name, patch) => {
        setSheetSettings((prev) => ({ ...prev, [name]: { ...prev[name], ...patch } }));
    };

    const confirmImport = async () => {
        if (!columnMap.name || !columnMap.roll_number) {
            toast.error("Map both Name and Roll Number columns before importing");
            return;
        }
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append(
                "mapping",
                JSON.stringify({
                    column_map: columnMap,
                    sheets: Object.entries(sheetSettings).map(([name, s]) => ({
                        name,
                        department_override: s.department_override,
                        bus_id: s.bus_id || null,
                    })),
                })
            );
            const { data } = await api.post("/students/import/commit", fd);
            setResults(data.results);
            toast.success("Import complete");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    const startOver = () => {
        setFile(null);
        setPreview(null);
        setColumnMap({});
        setSheetSettings({});
        setResults(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1100px]">
                <button
                    onClick={() => navigate("/admin/students")}
                    className="flex items-center gap-2 text-sm font-bold text-[#666666] hover:text-[#111111] mb-6"
                >
                    <ArrowLeft size={16} /> Back to Students
                </button>
                <p className="label-caps mb-2">Bulk Import</p>
                <h1 className="font-display text-5xl text-[#111111] mb-8">Import Students</h1>

                {!preview && (
                    <div className="bg-white border border-[#E5E5E5] p-8">
                        <p className="text-[#666666] mb-6">
                            Upload the Excel file. Each worksheet is treated as one department — its sheet name
                            becomes the department (editable in the next step).
                        </p>
                        <input
                            type="file"
                            ref={fileRef}
                            accept=".xlsx,.xls"
                            data-testid="import-students-file"
                            className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-3 text-sm mb-6"
                        />
                        <button
                            onClick={analyze}
                            disabled={analyzing}
                            data-testid="analyze-file-button"
                            className="bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015] flex items-center gap-2 disabled:opacity-60"
                        >
                            <Upload size={18} /> {analyzing ? "Analyzing…" : "Analyze File"}
                        </button>
                    </div>
                )}

                {preview && !results && (
                    <div className="space-y-8">
                        <div className="bg-white border border-[#E5E5E5] p-8">
                            <h2 className="font-display text-2xl mb-4">Column Mapping</h2>
                            <p className="text-sm text-[#666666] mb-6">
                                Applied to every worksheet. Only Name and Roll Number are required.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {FIELDS.map((f) => (
                                    <div key={f.key}>
                                        <label className="label-caps block mb-2">
                                            {f.label} {f.required && <span className="text-[#FF3B30]">*</span>}
                                        </label>
                                        <select
                                            data-testid={`map-${f.key}`}
                                            value={columnMap[f.key] || ""}
                                            onChange={(e) => setColumnMap({ ...columnMap, [f.key]: e.target.value || undefined })}
                                            className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                        >
                                            <option value="">— None —</option>
                                            {preview.all_headers.map((h) => (
                                                <option key={h} value={h}>
                                                    {h}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white border border-[#E5E5E5] p-8">
                            <h2 className="font-display text-2xl mb-4">Worksheets ({preview.sheets.length})</h2>
                            <div className="space-y-4">
                                {preview.sheets.map((s) => {
                                    const missing = FIELDS.filter(
                                        (f) => columnMap[f.key] && !s.headers.includes(columnMap[f.key])
                                    );
                                    return (
                                        <div key={s.name} className="border border-[#E5E5E5] p-4">
                                            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                                                <div>
                                                    <span className="font-bold">{s.name}</span>
                                                    <span className="text-[#666666] text-sm ml-2">{s.row_count} rows</span>
                                                </div>
                                                {missing.length > 0 && (
                                                    <span className="flex items-center gap-1 text-xs text-[#FF3B30] font-bold uppercase tracking-wider">
                                                        <AlertTriangle size={14} />
                                                        Missing: {missing.map((m) => m.label).join(", ")}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="label-caps block mb-2">Department</label>
                                                    <input
                                                        data-testid={`department-${s.name}`}
                                                        value={sheetSettings[s.name]?.department_override || ""}
                                                        onChange={(e) =>
                                                            updateSheetSetting(s.name, { department_override: e.target.value })
                                                        }
                                                        className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="label-caps block mb-2">Assign Bus (optional)</label>
                                                    <select
                                                        data-testid={`bus-${s.name}`}
                                                        value={sheetSettings[s.name]?.bus_id || ""}
                                                        onChange={(e) => updateSheetSetting(s.name, { bus_id: e.target.value })}
                                                        className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                                    >
                                                        <option value="">— None —</option>
                                                        {buses.map((b) => (
                                                            <option key={b.id} value={b.id}>
                                                                {b.bus_name} · {b.route_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={startOver}
                                className="border border-[#111111] font-bold px-6 py-3 hover:bg-[#F8F9FA]"
                            >
                                Start Over
                            </button>
                            <button
                                onClick={confirmImport}
                                disabled={importing}
                                data-testid="confirm-import-button"
                                className="bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015] disabled:opacity-60"
                            >
                                {importing ? "Importing…" : "Confirm & Import"}
                            </button>
                        </div>
                    </div>
                )}

                {results && (
                    <div className="space-y-6">
                        <div className="bg-[#34C759]/10 border border-[#34C759]/30 px-6 py-4 flex items-center gap-3">
                            <CheckCircle2 className="text-[#34C759]" size={22} />
                            <span className="font-bold">Import complete.</span>
                        </div>
                        {results.map((r) => (
                            <div key={r.sheet} className="bg-white border border-[#E5E5E5] p-6">
                                <h3 className="font-display text-xl mb-3">
                                    {r.sheet} <span className="text-[#666666] text-sm font-normal">→ {r.department}</span>
                                </h3>
                                <p className="text-sm mb-3">
                                    <span className="font-bold text-[#34C759]">{r.created}</span> created ·{" "}
                                    <span className="font-bold text-[#666666]">{r.updated}</span> updated ·{" "}
                                    <span className="font-bold text-[#FF3B30]">{r.skipped.length}</span> skipped ·{" "}
                                    <span className="font-bold text-[#34C759]">{r.qr_assigned}</span> QR assigned
                                </p>
                                {r.skipped.length > 0 && (
                                    <div className="text-xs text-[#666666] mb-2">
                                        Skipped: {r.skipped.map((s) => `Row ${s.row} (${s.reason})`).join("; ")}
                                    </div>
                                )}
                                {r.qr_warnings.length > 0 && (
                                    <div className="text-xs text-[#FF3B30]">
                                        {r.qr_warnings.join("; ")}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="flex gap-3">
                            <button
                                onClick={startOver}
                                className="border border-[#111111] font-bold px-6 py-3 hover:bg-[#F8F9FA]"
                            >
                                Import Another File
                            </button>
                            <button
                                onClick={() => navigate("/admin/students")}
                                data-testid="import-done-button"
                                className="bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015]"
                            >
                                View Students
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
